<template>
  <input
    v-bind="$attrs"
    :id="id"
    ref="input"
    :aria-invalid="invalid ? 'true' : 'false'"
    :value="value"
    :class="className"
    @input="handleInput"
  >
</template>

<script setup lang="ts">
import { useEventListener } from '@vueuse/core';
import { computed, useCssModule, useTemplateRef } from 'vue';
import widthStyles from '@/shared/legacy/ui/css/widths.module.css';
import { useVisibilityObserver } from '@/shared/lib/visibility-observer';

const { id = '', invalid = false, focus = false, big = false, size = 'full', arrowDisable = false, withTrim = false } = defineProps<{
  id?: string;
  value?: string | number;
  invalid?: boolean;
  focus?: boolean;
  big?: boolean;
  size?: 'date' | 'nano' | 'micro' | 'smaller' | 'small' | 'normal' | 'medium' | 'large' | 'larger' | 'big' | 'bigger' | 'giant' | 'titan' | 'period' | 'full';
  arrowDisable?: boolean;
  withTrim?: boolean;
}>();

const emits = defineEmits<{
  inputValue: [value: string];
  input: [event: Event];
}>();

const style = useCssModule();
const className = computed(() => {
  const sizeClassName = widthStyles[size];
  return {
    [style.formControl]: true,
    [sizeClassName]: Boolean(sizeClassName),
    [style.big]: big,
    [style.arrow]: arrowDisable,
    [style.invalid]: invalid
  };
});

const inputElement = useTemplateRef<HTMLInputElement>('input');

const handleInput = (event: Event) => {
  let inputValue = inputElement.value?.value || '';

  if (withTrim) {
    inputValue = inputValue.trim();
  }

  emits('inputValue', inputValue);
  emits('input', event);
};

const setFocus = () => {
  inputElement.value?.focus();
};

defineExpose({
  setFocus
});

useVisibilityObserver(inputElement, () => {
  if (focus) {
    setFocus();
  }
});
useEventListener(document.body, 'keydown', (event) => {
  // Исправляет ошибку в Safari, при которой после закрытия
  // модального окна через Esc, содержащее поле ввода с фокусом
  // (причём фокус при монтировании, после setFocus()),
  // может срабатывать непонятный скролл
  if (event.key === 'Escape' && document.activeElement === inputElement.value) {
    inputElement.value?.blur();
  }
});
</script>

<style module>
.formControl {
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  color: var(--content-primary, #050505);
  min-height: 40px;
  padding: 0 var(--Space-400, 16px);
  margin: 0;
  outline: 0 none;
  border-radius: var(--Radius-x2, 8px);
  border: 1px solid var(--border-neutral-M, rgba(0, 0, 0, 0.16));
  background-color: $white;
  align-items: center;
  width: 100%;
  display: inline-block;

  &:focus {
    border-color: var(--content-brand-blue, #25cfe8);
  }

  &[disabled] {
    opacity: 0.3;
    cursor: default;
    /* <3 Safari */
    -webkit-text-fill-color: var(--content-primary, #050505);
  }

  &.invalid {
    border-color: var(--content-danger, #f00);
  }
}

.big {
  padding-top: 9px;
  padding-bottom: 10px;
}
.arrows input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.arrows input[type='number'] {
  -moz-appearance: textfield;
}
</style>

<i18n lang="json">{}</i18n>
